import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import {
  ArrowLeft,
  Building,
  Users,
  UserCheck,
  Settings,
  Edit,
  Trash2,
  Plus,
  RefreshCw,
  ClipboardCheck,
  XCircle,
  ChevronDown,
  Mail,
  Phone,
  Globe,
  MapPin,
  Hash,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Shield,
  Eye,
} from 'lucide-react';
import { assetsApi } from '../api';
import { usersApi } from '../api/users.api';
import { notify } from '@core/notifications/notify';
import { useModal, AdminUserModal, ConfirmModal } from '@components/modal';
import { Company } from '../../../types';
import type { UserWithRoles } from '../api/users.api';
import { ApiError } from '../api/users.api';

/* ── Auth helpers ────────────────────────────────────────────────────────── */
function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1] as string) : null;
}
function getAuthHeaders(includeCsrf = false): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token');
  const headers: Record<string, string> = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  if (includeCsrf) { const csrf = getCsrfToken(); if (csrf) headers['X-CSRF-Token'] = csrf; }
  return headers;
}

/* ── Auditor API helpers ─────────────────────────────────────────────────── */
async function fetchAuditorAssignment(companyId: number): Promise<any | null> {
  try {
    const res = await fetch(`/api/rbac/auditor-assignments/company/${companyId}`, { headers: getAuthHeaders() });
    if (!res.ok) return null;
    const data = await res.json();
    return (data.data || []).find((a: any) => a.status === 'active') || null;
  } catch { return null; }
}
async function createAuditorAssignment(userId: number, companyId: number, reason: string): Promise<void> {
  const res = await fetch('/api/rbac/auditor-assignments', {
    method: 'POST', headers: getAuthHeaders(true),
    body: JSON.stringify({ userId, companyId, reason }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Failed to assign auditor');
}
async function revokeAuditorAssignment(assignmentId: number): Promise<void> {
  const res = await fetch(`/api/rbac/auditor-assignments/${assignmentId}/revoke`, {
    method: 'POST', headers: getAuthHeaders(true),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Failed to revoke assignment');
}

/* ── Tab type ────────────────────────────────────────────────────────────── */
type Tab = 'overview' | 'users' | 'auditor' | 'settings';

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <Building size={16} /> },
  { id: 'users', label: 'Users', icon: <Users size={16} /> },
  { id: 'auditor', label: 'External Auditor', icon: <UserCheck size={16} /> },
  { id: 'settings', label: 'Settings', icon: <Settings size={16} /> },
];

const inputCls = 'w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm';
const labelCls = 'block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide';

const INDUSTRY_OPTIONS = [
  'Manufacturing', 'Energy', 'Transportation', 'Agriculture',
  'Construction', 'Financial Services', 'Healthcare', 'Technology',
  'Retail', 'General',
];
const SIZE_OPTIONS = ['small', 'medium', 'large', 'enterprise'];

/* ══════════════════════════════════════════════════════════════════════════ */
const AdminCompanyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = Number(id);
  const { openModal, closeModal } = useModal();
  const { user: currentUser } = useAuth();
  const isSystemAdmin = !currentUser?.company_id;

  // Company admins can only manage their own company
  if (currentUser?.company_id && currentUser.company_id !== companyId) {
    return <Navigate to={`/admin/companies/${currentUser.company_id}`} replace />;
  }

  const [company, setCompany] = useState<Company | null>(null);
  const [companyUsers, setCompanyUsers] = useState<UserWithRoles[]>([]);
  const [externalAuditors, setExternalAuditors] = useState<any[]>([]);
  const [auditorAssignment, setAuditorAssignment] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [profileNames, setProfileNames] = useState<Record<number, string>>({});

  // Settings form state
  const [settingsForm, setSettingsForm] = useState<any>({});
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Auditor modal state
  const [selectedAuditorId, setSelectedAuditorId] = useState<number | ''>('');
  const [auditorReason, setAuditorReason] = useState('');
  const [auditorSaving, setAuditorSaving] = useState(false);

  /* ── Load data ─────────────────────────────────────────────────────────── */
  const loadCompany = useCallback(async () => {
    try {
      const found = await assetsApi.getCompany(companyId);
      if (found) {
        setCompany(found);
        setSettingsForm({
          name: found.name,
          legal_name: found.legal_name,
          industry: found.industry,
          size: found.size,
          registration_number: found.registration_number || '',
          address: found.address || '',
          phone: found.phone || '',
          email: found.email || '',
          website: found.website || '',
          is_active: found.is_active ?? true,
        });
      }
    } catch {
      notify.error('Failed to load company');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const loadCompanyUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const users = await usersApi.getUsersByCompany(companyId);
      setCompanyUsers(users);
    } catch {
      // non-critical
    } finally {
      setUsersLoading(false);
    }
  }, [companyId]);

  const loadAuditorInfo = useCallback(async () => {
    const [assignment, auditorsList] = await Promise.all([
      fetchAuditorAssignment(companyId),
      usersApi.getUsers(1, 200, '').then(r =>
        (r.users || []).filter((u: any) => !u.company_id && u.role?.toLowerCase().includes('auditor'))
      ).catch(() => []),
    ]);
    setAuditorAssignment(assignment);
    setExternalAuditors(auditorsList);
  }, [companyId]);

  useEffect(() => {
    loadCompany();
    loadCompanyUsers();
    loadAuditorInfo();
    usersApi.getUserTypes(true).then(profiles => {
      const map: Record<number, string> = {};
      profiles.forEach((p: any) => { map[p.id] = p.name; });
      setProfileNames(map);
    }).catch(() => {});
  }, [loadCompany, loadCompanyUsers, loadAuditorInfo]);

  /* ── Handlers ──────────────────────────────────────────────────────────── */
  const handleAssignAuditor = async () => {
    if (!selectedAuditorId) return;
    setAuditorSaving(true);
    try {
      await createAuditorAssignment(Number(selectedAuditorId), companyId, auditorReason || 'Assigned by system admin');
      notify.success('External auditor assigned');
      setSelectedAuditorId('');
      setAuditorReason('');
      await loadAuditorInfo();
    } catch (err: any) {
      notify.error(err.message || 'Failed to assign auditor');
    } finally {
      setAuditorSaving(false);
    }
  };

  const handleRevokeAuditor = async () => {
    if (!auditorAssignment) return;
    setAuditorSaving(true);
    try {
      await revokeAuditorAssignment(auditorAssignment.id);
      notify.success('Auditor assignment revoked');
      setAuditorAssignment(null);
    } catch (err: any) {
      notify.error(err.message || 'Failed to revoke assignment');
    } finally {
      setAuditorSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!company) return;
    setSettingsSaving(true);
    try {
      await assetsApi.updateCompany(company.id, settingsForm);
      notify.success('Company updated');
      await loadCompany();
    } catch {
      notify.error('Failed to update company');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleAddUser = () => {
    const modalId = openModal({
      component: AdminUserModal,
      props: {
        selectedUser: null,
        availableCompanies: company ? [company] : [],
        availablePermissions: [],
        hideCompanySelection: true,
        companyScoped: true,
        onSubmit: async (userData: any) => {
          try {
            await usersApi.createUser({
              email: userData.email,
              first_name: userData.first_name,
              last_name: userData.last_name,
              company_id: companyId,
              user_types: userData.user_types || [],
              permissionKeys: userData.permissions || [],
            });
            notify.success('User created');
            closeModal(modalId);
            loadCompanyUsers();
          } catch (err: any) {
            const is409 = err?.status === 409 || (err?.name === 'ApiError' && err?.data?.existingUser);
            if (is409 && err?.data?.existingUser) {
              const existing = err.data.existingUser;
              const existingName = existing.name || `${userData.first_name} ${userData.last_name}`;
              const existingCompany = existing.company_name
                ? `company "${existing.company_name}"`
                : 'no company assigned';
              // Defer to let AdminUserModal's onClose() run first, then open confirm
              setTimeout(() => {
                openModal({
                  component: ConfirmModal,
                  props: {
                    title: 'User Already Exists',
                    message: `${existingName} (${userData.email}) is already registered with ${existingCompany}. Do you want to assign them to this company instead?`,
                    confirmText: 'Assign to This Company',
                    type: 'warning',
                    onConfirm: async () => {
                      try {
                        await usersApi.updateUser(existing.id, { company_id: companyId });
                        notify.success(`${existingName} has been assigned to this company`);
                        loadCompanyUsers();
                      } catch (updateErr: any) {
                        notify.error(updateErr.message || 'Failed to assign user');
                      }
                    },
                    onCancel: () => {},
                  },
                });
              }, 100);
            } else {
              notify.error(err.message || 'Failed to create user');
            }
          }
        },
        onClose: () => closeModal(modalId),
      },
      options: { size: 'large' },
    });
  };

  const handleDeleteUser = (user: UserWithRoles) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Delete User',
        message: `Are you sure you want to delete ${user.first_name} ${user.last_name}?`,
        type: 'danger',
        onConfirm: async () => {
          try {
            await usersApi.deleteUser(user.id);
            notify.success('User deleted');
            loadCompanyUsers();
          } catch (err: any) {
            notify.error(err.message || 'Failed to delete user');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleEditUser = (user: UserWithRoles) => {
    const modalId = openModal({
      component: AdminUserModal,
      props: {
        selectedUser: user,
        availableCompanies: company ? [company] : [],
        availablePermissions: [],
        hideCompanySelection: true,
        companyScoped: true,
        onSubmit: async (userData: any) => {
          try {
            await usersApi.updateUser(user.id, {
              first_name: userData.first_name,
              last_name: userData.last_name,
              user_types: userData.user_types || [],
              is_active: userData.is_active,
            });
            notify.success('User updated');
            closeModal(modalId);
            loadCompanyUsers();
          } catch (err: any) {
            notify.error(err.message || 'Failed to update user');
          }
        },
        onClose: () => closeModal(modalId),
      },
      options: { size: 'large' },
    });
  };

  /* ── Loading ───────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <RefreshCw className="text-purple-500 animate-spin" size={36} />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <AlertTriangle className="text-amber-400" size={48} />
        <p className="text-slate-600 font-semibold">Company not found</p>
        <button onClick={() => navigate('/admin/companies')}
          className="flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium">
          <ArrowLeft size={16} /> Back to Companies
        </button>
      </div>
    );
  }

  /* ── Render ────────────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Hero Header ───────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-100/60 px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Back nav */}
          <button
            onClick={() => navigate(isSystemAdmin ? '/admin/companies' : '/company/dashboard')}
            className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-purple-700 mb-6 transition-colors group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            {isSystemAdmin ? 'All Companies' : 'Dashboard'}
          </button>

          <div className="flex items-start justify-between gap-6">
            <div className="flex items-center gap-5">
              {/* Company avatar */}
              <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center shrink-0">
                <span className="text-2xl font-bold text-white">
                  {company.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-bold text-slate-800 tracking-tight">{company.name}</h1>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${
                    company.is_active
                      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                      : 'bg-red-100 text-red-700 border-red-200'
                  }`}>
                    {company.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-slate-500 text-base">{company.legal_name}</p>
                <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                  {company.registration_number && (
                    <span className="flex items-center gap-1">
                      <Hash size={13} /> {company.registration_number}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar size={13} /> {new Date(company.created_at).toLocaleDateString()}
                  </span>
                  <span className="capitalize bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-bold">
                    {company.industry}
                  </span>
                  <span className="capitalize bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-bold">
                    {company.size}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick stats */}
            <div className="hidden lg:flex items-center gap-4 shrink-0">
              <div className="text-center px-5 py-3 bg-white rounded-3xl border border-slate-100 shadow-sm">
                <p className="text-2xl font-bold text-slate-800">{companyUsers.length}</p>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Users</p>
              </div>
              <div className="text-center px-5 py-3 bg-white rounded-3xl border border-slate-100 shadow-sm">
                <p className="text-2xl font-bold text-slate-800">{auditorAssignment ? '1' : '0'}</p>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Auditor</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex gap-1">
            {TABS.filter(tab => isSystemAdmin || tab.id !== 'auditor').map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Tab Content ───────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-8 py-8">

        {/* ── OVERVIEW TAB ────────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Company Details Card */}
            <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="bg-slate-50/60 px-6 py-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-purple-600 rounded-xl flex items-center justify-center">
                    <Building className="text-white" size={18} />
                  </div>
                  <h2 className="font-bold text-slate-800">Company Details</h2>
                </div>
              </div>
              <div className="p-6 grid grid-cols-2 gap-5">
                <InfoField icon={<Building size={14} />} label="Legal Name" value={company.legal_name} />
                <InfoField icon={<Hash size={14} />} label="Registration No." value={company.registration_number || '—'} />
                <InfoField icon={<Shield size={14} />} label="Industry" value={company.industry} />
                <InfoField icon={<Shield size={14} />} label="Size" value={company.size} capitalize />
                {company.email && <InfoField icon={<Mail size={14} />} label="Email" value={company.email} />}
                {company.phone && <InfoField icon={<Phone size={14} />} label="Phone" value={company.phone} />}
                {company.website && <InfoField icon={<Globe size={14} />} label="Website" value={company.website} />}
                {company.address && (
                  <div className="col-span-2">
                    <InfoField icon={<MapPin size={14} />} label="Address" value={company.address} />
                  </div>
                )}
              </div>
            </div>

            {/* Status & Quick Actions */}
            <div className="space-y-4">
              {/* Status Card */}
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="bg-slate-50/60 px-6 py-4 border-b border-slate-100">
                  <h2 className="font-bold text-slate-800">Status</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 font-medium">Account Status</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      company.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 font-medium">Total Users</span>
                    <span className="text-sm font-bold text-slate-800">{companyUsers.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 font-medium">External Auditor</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      auditorAssignment
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {auditorAssignment ? 'Assigned' : 'Not Assigned'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 font-medium">Created</span>
                    <span className="text-sm text-slate-500">{new Date(company.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="bg-slate-50/60 px-6 py-4 border-b border-slate-100">
                  <h2 className="font-bold text-slate-800">Quick Actions</h2>
                </div>
                <div className="p-4 space-y-2">
                  <button
                    onClick={() => setActiveTab('users')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-purple-50 transition-colors text-left group"
                  >
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                      <Plus size={15} className="text-purple-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700">Add User</span>
                  </button>
                  {isSystemAdmin && (
                  <button
                    onClick={() => setActiveTab('auditor')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-indigo-50 transition-colors text-left group"
                  >
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                      <UserCheck size={15} className="text-indigo-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700">Manage Auditor</span>
                  </button>
                  )}
                  <button
                    onClick={() => setActiveTab('settings')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors text-left group"
                  >
                    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                      <Edit size={15} className="text-slate-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700">Edit Details</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── USERS TAB ───────────────────────────────────────────────────── */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="bg-slate-50/60 px-8 py-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                    <Users className="text-white" size={20} />
                  </div>
                  <div>
                    <h2 className="font-bold text-slate-800">Company Users</h2>
                    <p className="text-sm text-slate-500">{companyUsers.length} users in this company</p>
                  </div>
                </div>
                <button
                  onClick={handleAddUser}
                  className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-blue-700 transition-all shadow-md hover:shadow-lg"
                >
                  <Plus size={16} />
                  Add User
                </button>
              </div>
            </div>

            {usersLoading ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="text-purple-400 animate-spin" size={28} />
              </div>
            ) : companyUsers.length === 0 ? (
              <div className="py-16 px-8">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-slate-400" size={32} />
                </div>
                <h3 className="text-lg font-bold text-slate-700 mb-2">No Users Yet</h3>
                <p className="text-slate-500 text-sm mb-6">This company doesn't have any users yet.</p>
                <button
                  onClick={handleAddUser}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all"
                >
                  <Plus size={16} /> Add First User
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Permission Profile</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Login</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {companyUsers.map(user => (
                      <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-left">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-800">
                                {user.first_name} {user.last_name}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left text-sm text-slate-600">{user.email}</td>
                        <td className="px-6 py-4 text-left">
                          {user.userTypes && user.userTypes.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.userTypes.map(t => (
                                <span key={t} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                                  {profileNames[t] || `Profile ${t}`}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">No profile</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-left">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] ${
                            user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {user.is_active ? <CheckCircle size={11} /> : <XCircle size={11} />}
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-left text-sm text-slate-500">
                          {user.last_login_at
                            ? new Date(user.last_login_at).toLocaleDateString()
                            : <span className="text-slate-300 italic">Never</span>}
                        </td>
                        <td className="px-6 py-4 text-left">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Edit user"
                            >
                              <Edit size={15} />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete user"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── AUDITOR TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'auditor' && (
          <div className="max-w-2xl">
            <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <ClipboardCheck className="text-blue-600" size={20} />
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">External Auditor Assignment</h2>
                    <p className="text-slate-500 text-sm">{company.name}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Current Assignment */}
                {auditorAssignment ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                    <p className="text-xs font-bold text-emerald-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <CheckCircle size={13} /> Currently Assigned
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-slate-800 text-lg">
                          {auditorAssignment.first_name} {auditorAssignment.last_name}
                        </p>
                        <p className="text-sm text-slate-500">{auditorAssignment.auditor_email}</p>
                        {auditorAssignment.reason && (
                          <p className="text-xs text-slate-400 mt-1 italic">"{auditorAssignment.reason}"</p>
                        )}
                      </div>
                      <button
                        onClick={handleRevokeAuditor}
                        disabled={auditorSaving}
                        className="flex items-center gap-1.5 text-sm font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-xl transition-all disabled:opacity-50"
                      >
                        <XCircle size={15} />
                        {auditorSaving ? 'Revoking…' : 'Revoke'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm text-amber-700 font-medium flex items-center gap-2">
                      <AlertTriangle size={15} />
                      No external auditor assigned to this company yet.
                    </p>
                  </div>
                )}

                {/* Assign / Replace */}
                <div className="space-y-4">
                  <h3 className="font-bold text-slate-700 text-sm">
                    {auditorAssignment ? 'Replace Auditor' : 'Assign External Auditor'}
                  </h3>

                  {externalAuditors.length === 0 ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
                      No external auditors available. Create a user with the{' '}
                      <strong>External Auditor</strong> profile first.
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className={labelCls}>Select Auditor</label>
                        <div className="relative">
                          <select
                            value={selectedAuditorId}
                            onChange={e => setSelectedAuditorId(e.target.value === '' ? '' : Number(e.target.value))}
                            className={inputCls + ' appearance-none pr-10'}
                          >
                            <option value="">— Select an auditor —</option>
                            {externalAuditors.map((a: any) => (
                              <option key={a.id} value={a.id}>
                                {a.first_name} {a.last_name} ({a.email})
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                        </div>
                      </div>

                      <div>
                        <label className={labelCls}>Reason (optional)</label>
                        <input
                          type="text"
                          value={auditorReason}
                          onChange={e => setAuditorReason(e.target.value)}
                          placeholder="e.g. Annual ISO 27001 certification audit"
                          className={inputCls}
                        />
                      </div>

                      <button
                        onClick={handleAssignAuditor}
                        disabled={!selectedAuditorId || auditorSaving}
                        className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
                      >
                        {auditorSaving ? <RefreshCw size={16} className="animate-spin" /> : <UserCheck size={16} />}
                        {auditorSaving ? 'Assigning…' : auditorAssignment ? 'Replace Auditor' : 'Assign Auditor'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── SETTINGS TAB ────────────────────────────────────────────────── */}
        {activeTab === 'settings' && (
          <div className="max-w-2xl">
            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="bg-slate-50/60 px-6 py-5 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                    <Settings className="text-white" size={20} />
                  </div>
                  <div>
                    <h2 className="font-bold text-slate-800">Edit Company</h2>
                    <p className="text-sm text-slate-500">Update company profile information</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Company Name *</label>
                    <input type="text" value={settingsForm.name || ''}
                      onChange={e => setSettingsForm((f: any) => ({ ...f, name: e.target.value }))}
                      className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Legal Name *</label>
                    <input type="text" value={settingsForm.legal_name || ''}
                      onChange={e => setSettingsForm((f: any) => ({ ...f, legal_name: e.target.value }))}
                      className={inputCls} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Industry</label>
                    <div className="relative">
                      <select value={settingsForm.industry || ''}
                        onChange={e => setSettingsForm((f: any) => ({ ...f, industry: e.target.value }))}
                        className={inputCls + ' appearance-none pr-10'}>
                        {INDUSTRY_OPTIONS.map(i => <option key={i}>{i}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Size</label>
                    <div className="relative">
                      <select value={settingsForm.size || ''}
                        onChange={e => setSettingsForm((f: any) => ({ ...f, size: e.target.value }))}
                        className={inputCls + ' appearance-none pr-10'}>
                        {SIZE_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Registration Number</label>
                    <input type="text" value={settingsForm.registration_number || ''}
                      onChange={e => setSettingsForm((f: any) => ({ ...f, registration_number: e.target.value }))}
                      className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input type="email" value={settingsForm.email || ''}
                      onChange={e => setSettingsForm((f: any) => ({ ...f, email: e.target.value }))}
                      className={inputCls} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input type="tel" value={settingsForm.phone || ''}
                      onChange={e => setSettingsForm((f: any) => ({ ...f, phone: e.target.value }))}
                      className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Website</label>
                    <input type="url" value={settingsForm.website || ''}
                      onChange={e => setSettingsForm((f: any) => ({ ...f, website: e.target.value }))}
                      className={inputCls} />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Address</label>
                  <textarea value={settingsForm.address || ''}
                    onChange={e => setSettingsForm((f: any) => ({ ...f, address: e.target.value }))}
                    rows={2}
                    className={inputCls + ' resize-none'} />
                </div>

                <div>
                  <label className={labelCls}>Status</label>
                  <div className="relative">
                    <select value={settingsForm.is_active ? 'active' : 'inactive'}
                      onChange={e => setSettingsForm((f: any) => ({ ...f, is_active: e.target.value === 'active' }))}
                      className={inputCls + ' appearance-none pr-10'}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleSaveSettings}
                    disabled={settingsSaving}
                    className="flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-700 transition-all disabled:opacity-50 shadow-md"
                  >
                    {settingsSaving ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    {settingsSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* ── Helper sub-component ────────────────────────────────────────────────── */
const InfoField: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  capitalize?: boolean;
}> = ({ icon, label, value, capitalize }) => (
  <div>
    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1 mb-1">
      {icon} {label}
    </p>
    <p className={`text-sm font-semibold text-slate-800 ${capitalize ? 'capitalize' : ''}`}>{value}</p>
  </div>
);

export default AdminCompanyDetail;
